/**
 * Put the two sides of a card into ONE picture.
 *
 * Two layouts, for two readers:
 * - "stacked" (front above back) is the picture that is SAVED as the policy's Aadhaar card. Unchanged from before it
 *   moved here, so the stored file looks the same as on every earlier policy.
 * - "sideBySide" (front left, back right) is the picture the AI reads. OpenAI shrinks a picture until its SHORT side is
 *   768 px; two landscape cards stacked make a tall picture whose short side is the card's width, so each side would
 *   reach the AI at about 40% of its detail. Side by side the short side is the card's height, so a normal card photo
 *   reaches it at full detail — the 12-digit number and the small address text stay readable.
 *
 * Lives outside the component so a test can replace it (jsdom has no canvas).
 */

// OpenAI fits a picture into 2048 x 2048 before reading it, so anything larger is only a bigger upload.
export const AI_MAX_SIDE = 2048;

/**
 * Where each side goes, and the canvas size. Pure — the canvas work below only follows it.
 *
 * @param {{width:number,height:number}} a - the front
 * @param {{width:number,height:number}} b - the back
 * @param {"stacked"|"sideBySide"} layout
 * @param {number} [maxSide] - scale the whole picture down so its longer side is at most this (never up)
 * @returns {{width:number,height:number,rects:Array<{x:number,y:number,w:number,h:number}>}}
 */
export const mergeLayout = (a, b, layout = "stacked", maxSide = Infinity) => {
  let width;
  let height;
  let rects;
  if (layout === "sideBySide") {
    width = a.width + b.width;
    height = Math.max(a.height, b.height);
    rects = [
      { x: 0, y: (height - a.height) / 2, w: a.width, h: a.height },
      { x: a.width, y: (height - b.height) / 2, w: b.width, h: b.height },
    ];
  } else {
    width = Math.max(a.width, b.width);
    height = a.height + b.height;
    rects = [
      { x: (width - a.width) / 2, y: 0, w: a.width, h: a.height },
      { x: (width - b.width) / 2, y: a.height, w: b.width, h: b.height },
    ];
  }

  const scale = Math.min(1, maxSide / Math.max(width, height));
  if (scale === 1) return { width, height, rects };
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    rects: rects.map((r) => ({ x: r.x * scale, y: r.y * scale, w: r.w * scale, h: r.h * scale })),
  };
};

/**
 * Merge two images (data URLs) into one JPEG blob.
 *
 * @param {string} frontImage
 * @param {string} backImage
 * @param {{layout?: "stacked"|"sideBySide", maxSide?: number}} [opts]
 */
export const mergeImages = (frontImage, backImage, { layout = "stacked", maxSide = Infinity } = {}) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img1 = new Image();
    const img2 = new Image();

    let loadedImages = 0;

    const checkBothLoaded = () => {
      loadedImages++;
      if (loadedImages === 2) {
        const { width, height, rects } = mergeLayout(img1, img2, layout, maxSide);
        canvas.width = width;
        canvas.height = height;

        // The AI copy gets a white background: where the two sides differ in height, an unpainted canvas turns black
        // in a JPEG, and a black band beside the text is one more thing for the reader to make sense of.
        if (layout === "sideBySide") {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
        }

        ctx.drawImage(img1, rects[0].x, rects[0].y, rects[0].w, rects[0].h);
        ctx.drawImage(img2, rects[1].x, rects[1].y, rects[1].w, rects[1].h);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create merged image"));
          }
        }, "image/jpeg", 0.9);
      }
    };

    img1.onload = checkBothLoaded;
    img2.onload = checkBothLoaded;

    img1.onerror = () => reject(new Error("Failed to load front image"));
    img2.onerror = () => reject(new Error("Failed to load back image"));

    img1.src = frontImage;
    img2.src = backImage;
  });
};
